import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  DEFAULT_PAGE_SIZE,
  Banner,
  Button,
  FilterChip,
  Kpi,
  Meter,
  Modal,
  PageHead,
  Pager,
  SearchInput,
  Segmented,
  StateBlock,
  useToast,
} from '@w7/ui'
import { roleFiltersFor, useAccount, useDownline, useSession } from '@/app/queries'
import { useDateRange } from '@/app/range'
import { AsOf, Money, exportCsv } from '@/app/bits'
import { useCreditStreamStatus } from '@/app/CreditStreamBridge'
import { ExportCsvButton } from '@/app/ExportCsvButton'
import { limitUsage, round2 } from '@/lib/book'
import { EMPTY, count, displayMoney, displayMoneyCompact, isoDay, lll, percent } from '@/lib/format'
import { ROLE_LABEL, type Account, type Role } from '@/data/types'
import type { DownlineQuery } from '@/data/api'
import {
  DownlineTable,
  SORTABLE,
  STATUS,
  selectionBlock,
  type SortDirection,
  type SortId,
} from './DownlineTable'
import { DownlineTree } from './DownlineTree'
import { DownlineOutline } from './DownlineOutline'
import { DownlineDetail } from './DownlineDetail'
import { TransferDialog } from './TransferDialog'
import { BulkSettleDialog } from './BulkSettleDialog'
import { isDirect } from './settle'
import { useStaffMe } from '@/api/hooks/shared/useStaffMe'
import { postGlobalLock, postSingleMemberLock } from '@/api/map/globalLock'
import {
  canBulkTransfer,
  canCreateDownline,
  canCreditTransfer,
  canGlobalLock,
} from '@/app/staffAccess'
import './downline.css'

type LockConfirm =
  | { kind: 'book'; unlocking: boolean }
  | { kind: 'member'; row: Account; unlocking: boolean }

/**
 * The downline — the screen the console is actually used from.
 *
 * The product being replaced opened here with a 120-row table whose columns
 * were: a checkbox, a row number, UserName, Credit Limit, Net Exposure (0 on
 * nearly every row), Take, Give, Status, and then seven icon-only columns whose
 * meanings could only be learnt by clicking them.
 *
 * It now splits into three HONEST views rather than one ambiguous list:
 *
 *   · Direct only — the SETTLING surface. Row selection, labelled Give and Take
 *     buttons, click-to-zero settlement, bulk settle. Everything here is
 *     possible only because credit moves between you and a direct child.
 *   · Whole downline — a real lazily-loaded treegrid, for BROWSING. No give, no
 *     take, no selection, and one quiet line saying why instead of a hundred
 *     rows of disabled buttons.
 *   · Hierarchy — split pane: slim outline tree + detail (KPIs, Give/Take when
 *     the selected node is a direct child, children table).
 *
 * Plus one derived case: whole downline or hierarchy WITH a filter renders the
 * flat table read-only, as a match list. A filter produces a list; browsing
 * produces a tree. Rendering a lazily-loaded tree that is also filtered would
 * mean fetching ancestors nobody expanded, and a tree with holes in it is worse
 * than a list.
 */

const PAGE_SIZE = DEFAULT_PAGE_SIZE
/** Keeps `?open=` sendable. Oldest expansions drop out first. */
const MAX_OPEN = 20
/** One click cannot tick an unbounded set; past this the operator settles in batches. */
const SELECT_ALL_LIMIT = 500

type Scope = 'direct' | 'all' | 'hierarchy'
type Mode = 'direct' | 'tree' | 'hierarchy' | 'match'

export function DownlinePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { range } = useDateRange()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: session } = useSession()
  const toast = useToast()
  const self = session?.account
  const creditStream = useCreditStreamStatus()
  const staffMe = useStaffMe()
  const qc = useQueryClient()
  const allowCreateDownline = staffMe.isSuccess && canCreateDownline(staffMe.data)
  const allowBulkTransfer = staffMe.isSuccess && canBulkTransfer(staffMe.data)
  const allowCreditTransfer = staffMe.isSuccess && canCreditTransfer(staffMe.data)
  const allowGlobalLock = staffMe.isSuccess && canGlobalLock(staffMe.data)
  const bookLocked = self?.globalLock === true
  const [lockConfirm, setLockConfirm] = useState<LockConfirm | null>(null)

  const invalidateDownline = () => {
    void qc.invalidateQueries({ queryKey: ['downline'] })
    void qc.invalidateQueries({ queryKey: ['session'] })
    void qc.invalidateQueries({ queryKey: ['account'] })
  }

  const globalLockMut = useMutation({
    mutationFn: () => postGlobalLock(),
    onSuccess: (res) => {
      toast.success(res.message)
      setLockConfirm(null)
      invalidateDownline()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Global lock failed')
    },
  })

  const memberLockMut = useMutation({
    mutationFn: (memberId: string) => postSingleMemberLock(memberId),
    onSuccess: (res) => {
      toast.success(res.message)
      setLockConfirm(null)
      invalidateDownline()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Global lock failed')
    },
  })
  const lockBusy = globalLockMut.isPending || memberLockMut.isPending

  /* The whole filter set lives in the URL, so "look at these six" is a link
     rather than a list of instructions. The Overview's attention banner arrives
     here as ?attention=1 and finds its filter already on. */
  const search = searchParams.get('q') ?? ''
  const roleFilters = roleFiltersFor(self?.role)
  const roles = searchParams
    .getAll('role')
    .filter((value): value is Role => roleFilters.some((filter) => filter.id === value))
  const needsAttention = searchParams.get('attention') === '1'
  const hasExposure = searchParams.get('exposure') === '1'
  const settlementParam = searchParams.get('settlement')
  const settlement =
    settlementParam === 'collect' || settlementParam === 'pay' ? settlementParam : undefined
  const page = Math.max(1, Number(searchParams.get('page')) || 1)
  const openIds = (searchParams.get('open') ?? '').split(',').filter(Boolean)

  /**
   * Landing intents from the ⌘K palette.
   *
   * `?transfer=give|take&account=<id>` and `?settle=1` are the palette's four
   * contextual rows and its bulk-settle row. They are INTENTS, not state: each
   * is consumed once and stripped from the URL, because a param that survives
   * would reopen the dialog every time the operator closed it, and a bookmarked
   * link would raise a money dialog on every visit.
   *
   * Neither moves money. `transfer` opens the transfer popup with the direction
   * pre-picked and the amount blank; `settle` selects the outstanding pool and
   * opens the PLAN step. That is the palette's own rule — an ⏎ on the wrong row
   * must land you on the screen that asks you to confirm, never past it.
   */
  const transferParam = searchParams.get('transfer')
  const transferIntent: 'give' | 'take' | null =
    transferParam === 'give' || transferParam === 'take' ? transferParam : null
  const transferAccountId = searchParams.get('account')
  const wantsBulkSettle = searchParams.get('settle') === '1'

  const sortParam = searchParams.get('sort')
  const sort: SortId = SORTABLE.find((entry) => entry.id === sortParam)?.id ?? 'username'
  const dirParam = searchParams.get('dir')
  const direction: SortDirection =
    dirParam === 'asc' || dirParam === 'desc'
      ? dirParam
      : (SORTABLE.find((entry) => entry.id === sort)?.defaultDirection ?? 'asc')

  const filtersActive = Boolean(
    search || roles.length || needsAttention || hasExposure || settlement,
  )

  /**
   * The default flips from the old `all` to `direct`: this page's job is give,
   * take and settle, and every one of those only exists on a direct child.
   *
   * `scope` is derived exactly the way the normalising effect writes it, so the
   * first render is already right and the URL rewrite changes nothing on
   * screen. A default computed from state that changes as the operator types
   * would swap the whole view out from under them mid-search, which is why it
   * is normalised once on mount rather than derived on every render.
   */
  const scopeParam = searchParams.get('scope')
  const scope: Scope =
    scopeParam === 'all'
      ? 'all'
      : scopeParam === 'hierarchy'
        ? 'hierarchy'
        : scopeParam === 'direct'
          ? 'direct'
          : filtersActive
            ? 'all'
            : 'direct'
  const mode: Mode =
    scope === 'direct'
      ? 'direct'
      : scope === 'hierarchy'
        ? 'hierarchy'
        : filtersActive
          ? 'match'
          : 'tree'

  const normalised = useRef(false)
  useEffect(() => {
    if (normalised.current || searchParams.get('scope')) return
    normalised.current = true
    const params = new URLSearchParams(searchParams)
    // Filters already in the URL mean somebody linked here to see specific
    // people wherever they sit — the Overview's ?attention=1 link does exactly
    // that — so they get the whole downline, not just the direct rung.
    params.set('scope', filtersActive ? 'all' : 'direct')
    setSearchParams(params, { replace: true })
  }, [searchParams, setSearchParams, filtersActive])

  // Density is global (Appearance → Table density), not part of the view URL.
  const [target, setTarget] = useState<{
    account: Account
    direction: 'give' | 'take'
    amount?: number
  } | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [truncated, setTruncated] = useState<number | null>(null)

  /**
   * Selection is a Map keyed by id, not a Set of ids, so the bar can describe
   * rows that have scrolled onto another page. It survives pagination, sorting
   * and filter changes; only the operator, a completed run, or leaving the page
   * clears it.
   */
  const [selection, setSelection] = useState<Map<string, Account>>(() => new Map())
  const selectedKeys = useMemo(() => new Set(selection.keys()), [selection])
  const selectedRows = useMemo(() => [...selection.values()], [selection])

  const patch = (next: Record<string, string | string[] | null>) => {
    const params = new URLSearchParams(searchParams)
    for (const [key, value] of Object.entries(next)) {
      params.delete(key)
      if (Array.isArray(value)) for (const entry of value) params.append(key, entry)
      else if (value !== null) params.set(key, value)
    }
    // replace, not push: typing four characters into the search box should not
    // cost four presses of the back button.
    setSearchParams(params, { replace: true })
  }

  const toggleRole = (role: Role) => {
    const next = roles.includes(role) ? roles.filter((entry) => entry !== role) : [...roles, role]
    patch({ role: next, page: null })
  }

  /* --------------------------------------------------- palette landings -- */

  /**
   * Fetched by id rather than looked up in `rows`.
   *
   * The palette's link carries `?q=<username>` too, so the row is USUALLY on
   * screen — but "usually" is not a contract. A duplicate-ish username, a stale
   * link, or a second page and the lookup silently finds nothing and the dialog
   * never opens, with no way to tell that apart from "the link was wrong".
   * `getAccount` is scope-checked in the data layer, so an id outside the
   * subtree resolves to nothing here exactly as it should.
   */
  const deepLinkAccount = useAccount(
    transferIntent && transferAccountId ? transferAccountId : undefined,
  )

  const consumed = useRef(false)
  useEffect(() => {
    if (consumed.current || !transferIntent || !transferAccountId || !self) return
    if (deepLinkAccount.isLoading) return
    // Wait for /v1/me so staff Give/Take is known before opening the dialog.
    if (!staffMe.isSuccess) return

    consumed.current = true
    const account = deepLinkAccount.data
    // Directive 4 is enforced here as well as on the row: give and take exist
    // only between you and a DIRECT child, so a link naming a grandchild is
    // refused with the reason rather than opening a dialog that cannot submit.
    if (!allowCreditTransfer) {
      toast.push({
        tone: 'warn',
        message: 'Missing access grant: Give/Take',
      })
    } else if (account && isDirect(account, self)) {
      setTarget({ account, direction: transferIntent })
    } else {
      toast.push({
        tone: 'warn',
        message: account
          ? `Credit cannot move directly to ${account.username}`
          : 'That account could not be opened',
        detail: account
          ? 'Give and take apply to your direct children only. Credit reaches a deeper account through the agent above it.'
          : 'It may have been removed, or it sits outside your downline.',
      })
    }
    patch({ transfer: null, account: null })
    // `patch` closes over `searchParams` and would re-run this on every URL
    // change; the ref is what makes it fire exactly once per landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    transferIntent,
    transferAccountId,
    self,
    deepLinkAccount.isLoading,
    deepLinkAccount.data,
    staffMe.isSuccess,
    allowCreditTransfer,
  ])

  const filters: DownlineQuery = {
    search: search || undefined,
    roles: roles.length ? roles : undefined,
    needsAttention: needsAttention || undefined,
    hasExposure: hasExposure || undefined,
    settlement,
    directOnly: scope === 'direct',
  }

  const { data, isLoading, isError, error, refetch, dataUpdatedAt } = useDownline({
    ...filters,
    page,
    pageSize: PAGE_SIZE,
    sort,
    direction,
  })

  /**
   * One auxiliary list, three jobs, keyed differently per mode so nothing is
   * fetched that the mode on screen does not use:
   *   tree   — the root level, unpaged, in the current sort
   *   direct — the pool that "Select all N outstanding" ticks from
   *   match  — the usernames the lineage line under each row is built from
   */
  const aux = useDownline(
    mode === 'match'
      ? { directOnly: false, pageSize: 1000 }
      : mode === 'tree' || mode === 'hierarchy'
        ? { directOnly: true, pageSize: SELECT_ALL_LIMIT, sort, direction }
        : { directOnly: true, settlement, pageSize: SELECT_ALL_LIMIT },
  )

  /**
   * Two one-row queries, purely for the counts in the settlement KPI meta.
   * `totals` carries the sums but not how many accounts they are spread across,
   * and "₹4.6L owed by 23 accounts" is a different Monday from "₹4.6L owed by
   * one". Sort stays out of the key — it cannot change a total.
   */
  const countBase = { ...filters, settlement: undefined, page: 1, pageSize: 1 }
  const collectCount = useDownline({ ...countBase, settlement: 'collect' })
  const payCount = useDownline({ ...countBase, settlement: 'pay' })
  // An active filter for one direction makes the other side's total zero, so
  // its count has to read zero too or the tile contradicts its own figure.
  const collectAccounts = settlement === 'pay' ? 0 : collectCount.data?.total
  const payAccounts = settlement === 'collect' ? 0 : payCount.data?.total

  const rows = data?.rows ?? []
  const totals = data?.totals
  const issuedUsage = totals && totals.creditLimit > 0 ? totals.exposure / totals.creditLimit : 0
  const drawn = totals && totals.creditLimit > 0 ? totals.balance / totals.creditLimit : 0

  /* ------------------------------------------------------------ selection -- */

  const onSelectionChange = (next: Set<string>) => {
    setSelection((current) => {
      const merged = new Map<string, Account>()
      for (const id of next) {
        // An id already in the map may belong to a row on another page, so the
        // existing entry wins over a lookup that can only see this page.
        const account = current.get(id) ?? rows.find((row) => row.id === id)
        if (account) merged.set(id, account)
      }
      return merged
    })
  }

  /**
   * The rows "Select all N outstanding" would actually tick.
   *
   * The pool's own `total` is the wrong number to put on that button: without a
   * settlement chip active the pool is every direct child, most of whom have
   * nothing outstanding. A button offering to select 40 that then ticks 23 is a
   * button nobody trusts a second time.
   */
  const outstandingPool = useMemo(
    () => (aux.data?.rows ?? []).filter((row) => selectionBlock(row, self) === null),
    [aux.data, self],
  )

  const selectAllOutstanding = () => {
    setSelection((current) => {
      const merged = new Map(current)
      for (const row of outstandingPool.slice(0, SELECT_ALL_LIMIT)) merged.set(row.id, row)
      return merged
    })
    setTruncated((aux.data?.total ?? 0) > SELECT_ALL_LIMIT ? (aux.data?.total ?? 0) : null)
  }

  /**
   * `?settle=1` — the palette's "Bulk settle outstanding".
   *
   * Waits for the pool rather than firing on mount: opening the plan against a
   * selection of zero would show an empty run and read as "nothing is
   * outstanding", which is a claim about the book made from a loading state.
   * An empty pool is reported as the good news it is instead of an empty dialog.
   */
  const settleConsumed = useRef(false)
  useEffect(() => {
    if (settleConsumed.current || !wantsBulkSettle || !self) return
    if (!staffMe.isSuccess) return
    if (mode !== 'direct' || aux.isLoading || !aux.data) return

    settleConsumed.current = true
    if (!allowBulkTransfer) {
      toast.push({
        tone: 'warn',
        message: 'Missing access grant: Bulk settle',
      })
    } else if (outstandingPool.length > 0) {
      selectAllOutstanding()
      setBulkOpen(true)
    } else {
      toast.push({
        tone: 'info',
        message: 'Nothing is outstanding',
        detail: 'Every direct child is already settled to zero.',
      })
    }
    patch({ settle: null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wantsBulkSettle,
    self,
    mode,
    aux.isLoading,
    aux.data,
    outstandingPool.length,
    staffMe.isSuccess,
    allowBulkTransfer,
  ])

  const selectionSums = selectedRows.reduce(
    (acc, row) => {
      if (row.settlement > 0) acc.collect += row.settlement
      else acc.pay += -row.settlement
      return acc
    },
    { collect: 0, pay: 0 },
  )
  const selectionNet = round2(selectionSums.collect - selectionSums.pay)
  const canSelectAll =
    outstandingPool.length > selection.size &&
    (Boolean(settlement) || (data ? data.total > rows.length : false))

  /* ----------------------------------------------------------- match list -- */

  const lineageNames = useMemo(() => {
    if (mode !== 'match') return null
    const map = new Map<string, string>()
    for (const row of aux.data?.rows ?? []) map.set(row.id, row.username)
    if (self) map.set(self.id, self.username)
    return map
  }, [mode, aux.data, self])

  const lineageOf = (row: Account): string | null => {
    if (!lineageNames || !self) return null
    const below = row.path.slice(row.path.indexOf(self.id) + 1)
    const names = below.slice(-2).flatMap((id) => {
      const name = lineageNames.get(id)
      return name ? [name] : []
    })
    return names.length ? `under ${names.join(' › ')}` : null
  }

  const showInTree = (row: Account) => {
    // `path` is already "ancestor ids, root first, excluding self", so the tree
    // needs no API of its own to know which levels to open.
    const below = self ? row.path.slice(row.path.indexOf(self.id) + 1) : row.path
    const params = new URLSearchParams({ scope: 'all' })
    if (below.length) params.set('open', below.slice(-MAX_OPEN).join(','))
    navigate({ pathname: '/downline', search: params.toString(), hash: `row-${row.id}` })
  }

  const focusParam = searchParams.get('focus')
  const rootRows = aux.data?.rows ?? []
  const focusFromRoots = focusParam ? rootRows.find((row) => row.id === focusParam) : undefined
  const needsDeepFocus =
    mode === 'hierarchy' && Boolean(focusParam) && !focusFromRoots && !aux.isLoading
  const deepFocus = useAccount(needsDeepFocus ? (focusParam ?? undefined) : undefined)
  const hierarchyAccount: Account | null =
    mode !== 'hierarchy'
      ? null
      : (focusFromRoots ??
        (needsDeepFocus ? (deepFocus.data ?? null) : null) ??
        (!focusParam ? (rootRows[0] ?? null) : null))
  const hierarchySelectedId = hierarchyAccount?.id ?? focusParam

  useEffect(() => {
    if (mode !== 'hierarchy' || focusParam || aux.isLoading || !rootRows[0]) return
    patch({ focus: rootRows[0].id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, focusParam, aux.isLoading, rootRows[0]?.id])

  const onExpandedChange = (id: string, open: boolean) => {
    const next = open
      ? [...openIds.filter((entry) => entry !== id), id]
      : openIds.filter((entry) => entry !== id)
    const capped = next.slice(-MAX_OPEN)
    patch({ open: capped.length ? capped.join(',') : null })
  }

  const landOn = location.hash.startsWith('#row-') ? location.hash.slice(5) : undefined

  /* ---------------------------------------------------------------- empty -- */

  const empty = filtersActive ? (
    <StateBlock
      icon="search"
      title="Nobody beneath you matches these filters"
      actions={
        <Button
          variant="secondary"
          icon="close"
          onClick={() => setSearchParams(new URLSearchParams({ scope }), { replace: true })}
        >
          Clear filters
        </Button>
      }
    >
      Widen the search, or drop a role, settlement or attention filter.
    </StateBlock>
  ) : (
    <StateBlock
      icon="downline"
      title="You have nobody beneath you yet"
      actions={
        allowCreateDownline ? (
          <Button variant="primary" icon="plus" onClick={() => navigate('/create')}>
            Add to downline
          </Button>
        ) : undefined
      }
    >
      Agents and members you create appear here with their credit limit, balance, exposure and
      settlement position.
    </StateBlock>
  )

  if (isError) {
    return (
      <StateBlock
        icon="error"
        tone="error"
        title="Your downline could not be loaded"
        actions={
          <Button variant="secondary" icon="refresh" onClick={() => refetch()}>
            Try again
          </Button>
        }
      >
        {(error as Error)?.message}
      </StateBlock>
    )
  }

  return (
    <>
      <PageHead
        title="Member List"
        subtitle={
          totals
            ? `${count(totals.agents)} agents and ${count(totals.members)} members in view · balances and exposure are current positions, not the ${range.label} window`
            : 'Every agent and member beneath you, with their credit, exposure and settlement position.'
        }
        actions={
          <>
            <AsOf at={dataUpdatedAt || undefined} stream={creditStream === 'open'} />
            {allowGlobalLock ? (
              <Button
                variant={bookLocked ? 'danger' : 'secondary'}
                disabled={lockBusy}
                onClick={() => setLockConfirm({ kind: 'book', unlocking: bookLocked })}
              >
                {bookLocked ? 'Global Unlock' : 'Global Lock'}
              </Button>
            ) : staffMe.isSuccess && bookLocked ? (
              <Button variant="danger" disabled title="View only — missing Global Lock grant">
                Globally locked
              </Button>
            ) : null}
            {allowCreateDownline ? (
              <Button icon="plus" variant="primary" onClick={() => navigate('/create')}>
                Add to downline
              </Button>
            ) : null}
          </>
        }
      />

      {/* --------------------------------------------------------- summary -- */}
      <div className="w7-grid w7-grid-5" style={{ marginBottom: 'var(--w7-s5)' }}>
        <Kpi
          label="Credit issued"
          value={totals ? displayMoneyCompact(totals.creditLimit, self?.currencyType) : EMPTY}
          exact={displayMoney(totals?.creditLimit, self?.currencyType)}
          meta={totals ? `Ceiling across ${count(totals.agents + totals.members)} in view` : undefined}
        />
        <Kpi
          label="Held by downline"
          value={totals ? displayMoneyCompact(totals.balance, self?.currencyType) : EMPTY}
          exact={displayMoney(totals?.balance, self?.currencyType)}
          meta={totals ? `${percent(drawn, 0)} of the credit you issued` : undefined}
        />
        <Kpi
          label="Open exposure"
          value={totals ? displayMoneyCompact(totals.exposure, self?.currencyType) : EMPTY}
          exact={displayMoney(totals?.exposure, self?.currencyType)}
          tone={issuedUsage > 1 ? 'loss' : issuedUsage > 0.8 ? 'warn' : 'neutral'}
          meta={
            totals ? (
              <>
                <Meter value={issuedUsage} label={`${percent(issuedUsage, 0)} of issued credit`} />
                {percent(issuedUsage, 0)} of issued credit
              </>
            ) : undefined
          }
        />
        <Kpi
          label="To collect"
          value={totals ? displayMoneyCompact(totals.toCollect, self?.currencyType) : EMPTY}
          exact={displayMoney(totals?.toCollect, self?.currencyType)}
          tone="profit"
          meta={
            collectAccounts === undefined
              ? 'Owed up to you, not yet swept'
              : `Owed up to you across ${count(collectAccounts)} ${collectAccounts === 1 ? 'account' : 'accounts'}. Filter with "They owe you".`
          }
        />
        <Kpi
          label="To pay"
          value={totals ? displayMoneyCompact(totals.toPay, self?.currencyType) : EMPTY}
          exact={displayMoney(totals?.toPay, self?.currencyType)}
          tone="loss"
          meta={
            payAccounts === undefined
              ? 'Owed by you down the chain'
              : `Owed by you down the chain across ${count(payAccounts)} ${payAccounts === 1 ? 'account' : 'accounts'}.`
          }
        />
      </div>

      {/* --------------------------------------------------------- toolbar -- */}
      <div className="w7-row w7-row-wrap" style={{ marginBottom: 'var(--w7-s4)' }}>
        <SearchInput
          label="Search your downline by username or name"
          placeholder="Username or name"
          value={search}
          width={228}
          onChange={(next) => patch({ q: next || null, page: null })}
        />

        {roleFilters.map((filter) => (
          <FilterChip
            key={filter.id}
            active={roles.includes(filter.id)}
            onClick={() => toggleRole(filter.id)}
          >
            {filter.label}
          </FilterChip>
        ))}
        <FilterChip
          active={needsAttention}
          onClick={() => patch({ attention: needsAttention ? null : '1', page: null })}
        >
          Needs attention
        </FilterChip>
        <FilterChip
          active={hasExposure}
          onClick={() => patch({ exposure: hasExposure ? null : '1', page: null })}
        >
          Has exposure
        </FilterChip>
        {/* The Monday pair. Mutually exclusive — "owed both ways" is not a set,
            and each chip clears the other rather than intersecting to nothing. */}
        <FilterChip
          active={settlement === 'collect'}
          onClick={() =>
            patch({ settlement: settlement === 'collect' ? null : 'collect', page: null })
          }
        >
          They owe you
        </FilterChip>
        <FilterChip
          active={settlement === 'pay'}
          onClick={() => patch({ settlement: settlement === 'pay' ? null : 'pay', page: null })}
        >
          You owe them
        </FilterChip>

        <div className="w7-row w7-row-wrap" style={{ marginLeft: 'auto' }}>
          <Segmented
            label="How far down to look"
            value={scope}
            onChange={(next) => patch({ scope: next, page: null, open: null, focus: null })}
            items={[
              { id: 'direct', label: 'Direct only' },
              { id: 'all', label: 'Whole downline' },
              { id: 'hierarchy', label: 'Hierarchy' },
            ]}
          />
          <ExportCsvButton
            disabled={rows.length === 0}
            count={rows.length}
            title="Downloads the rows on this page as CSV"
            onClick={() =>
              exportCsv(
                `w7-downline-${isoDay(Date.now())}.csv`,
                rows.map((row) => ({
                  Username: row.username,
                  Name: row.displayName,
                  Role: ROLE_LABEL[row.role],
                  Status: STATUS[row.status].label,
                  'Credit limit': row.creditLimit,
                  Balance: row.balance,
                  Exposure: row.exposure,
                  'Limit used': percent(limitUsage(row.exposure, row.creditLimit), 0),
                  Settlement: row.settlement,
                  'Last active': row.lastActiveAt ? lll(row.lastActiveAt) : '',
                  Attention: row.flags.join(' '),
                })),
              )
            }
          />
        </div>
      </div>

      {/*
        One quiet line saying what you are looking at and, in the tree, why it
        has no buttons. A <p>, not a Banner — a Banner announces something that
        CHANGED; this is a permanent fact about the view you chose.
      */}
      <p className="w7-dl-note">
        {mode === 'direct' ? (
          <>
            Your {count(data?.total ?? 0)} direct children. Credit moves between you and them only.
          </>
        ) : mode === 'tree' ? (
          <>
            Your whole downline as a tree. Credit only moves between you and your direct children,
            so this view is read-only.{' '}
            <button
              type="button"
              className="w7-link"
              onClick={() => patch({ scope: 'direct', page: null, open: null, focus: null })}
            >
              Switch to Direct only to give, take or settle.
            </button>
          </>
        ) : mode === 'hierarchy' ? (
          <>
            Browse the hierarchy; Give and Take appear for your direct children in the detail panel.
          </>
        ) : (
          <>
            {count(data?.total ?? 0)} matches across your whole downline. Give, take and settle are
            available on your direct children only.
          </>
        )}
      </p>

      {/* --------------------------------------------------- selection bar -- */}
      {mode === 'direct' && allowBulkTransfer && selection.size > 0 ? (
        <>
          <div className="w7-selectbar" role="region" aria-label="Bulk settlement">
            <span className="w7-selectbar-count" aria-live="polite">
              {count(selection.size)} selected
            </span>
            {/* The sums are what zeroing REQUIRES, not what the caps will allow.
                The bar states intent; the plan is where caps first appear. A
                capped figure here would disagree with the plan and the operator
                would not know which to believe. */}
            <span className="w7-selectbar-sums">
              {/* `pay` is toned red without being NEGATED: a minus sign next
                  to the word "pay" says the same thing twice and reads as a
                  refund. The tone carries the direction, the label names it. */}
              collect{' '}
              <Money
                value={selectionSums.collect}
                tone="profit"
                currency={self?.currencyType}
                alreadyDisplay
              />{' '}
              · pay{' '}
              <Money
                value={selectionSums.pay}
                tone="loss"
                currency={self?.currencyType}
                alreadyDisplay
              />{' '}
              · net{' '}
              <Money value={selectionNet} signed currency={self?.currencyType} alreadyDisplay />{' '}
              {selectionNet >= 0 ? 'to you' : 'from you'}
            </span>
            <span className="w7-selectbar-actions">
              <Button
                variant="ghost"
                onClick={() => {
                  setSelection(new Map())
                  setTruncated(null)
                }}
              >
                Clear
              </Button>
              {canSelectAll ? (
                <Button variant="secondary" busy={aux.isLoading} onClick={selectAllOutstanding}>
                  Select all {count(outstandingPool.length)} outstanding
                </Button>
              ) : null}
              <Button variant="primary" icon="transfers" onClick={() => setBulkOpen(true)}>
                Settle {count(selection.size)} to zero
              </Button>
            </span>
          </div>
          {truncated ? (
            <Banner tone="warn">
              Selected the first {count(SELECT_ALL_LIMIT)} of {count(truncated)}. Settle in batches.
            </Banner>
          ) : null}
        </>
      ) : null}

      {/* ------------------------------------------------------------ body -- */}
      {mode === 'hierarchy' ? (
        <div className="w7-dl-split">
          <DownlineOutline
            roots={rootRows}
            loading={aux.isLoading}
            expanded={new Set(openIds)}
            selectedId={hierarchySelectedId}
            onExpandedChange={onExpandedChange}
            onSelect={(id) => patch({ focus: id })}
            landOn={landOn}
            empty={empty}
          />
          <DownlineDetail
            account={hierarchyAccount}
            self={self}
            loadingAccount={Boolean(needsDeepFocus && deepFocus.isLoading)}
            allowCreditTransfer={allowCreditTransfer}
            onTransfer={(account, transferDirection, amount) =>
              setTarget({ account, direction: transferDirection, amount })
            }
            onSwitchDirect={() => patch({ scope: 'direct', page: null, open: null, focus: null })}
          />
        </div>
      ) : mode === 'tree' ? (
        <DownlineTree
          roots={rootRows}
          loading={aux.isLoading}
          expanded={new Set(openIds)}
          onExpandedChange={onExpandedChange}
          landOn={landOn}
          empty={empty}
        />
      ) : (
        <>
          <DownlineTable
            rows={rows}
            totals={totals}
            self={self}
            loading={isLoading}
            sort={sort}
            direction={direction}
            onSort={(id, next) => patch({ sort: id, dir: next, page: null })}
            selection={
              // Its absence is what removes bulk settle from the match list: a
              // select-all over a mix of selectable and un-selectable rows is
              // exactly the ambiguity this redesign exists to remove.
              mode === 'direct' && allowBulkTransfer
                ? { selected: selectedKeys, onChange: onSelectionChange }
                : undefined
            }
            lineageOf={mode === 'match' ? lineageOf : undefined}
            onShowInTree={mode === 'match' ? showInTree : undefined}
            onTransfer={(account, transferDirection, amount) =>
              setTarget({ account, direction: transferDirection, amount })
            }
            bookLocked={bookLocked}
            onToggleRowGlobalLock={(row, unlocking) =>
              setLockConfirm({ kind: 'member', row, unlocking })
            }
            empty={empty}
          />

          <Pager
            page={page}
            pageCount={Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE))}
            total={data?.total ?? 0}
            pageSize={PAGE_SIZE}
            onChange={(next) => patch({ page: next > 1 ? String(next) : null })}
          />
        </>
      )}

      {target && self && allowCreditTransfer && isDirect(target.account, self) ? (
        <TransferDialog
          self={self}
          account={target.account}
          initialDirection={target.direction}
          initialAmount={target.amount}
          onClose={() => setTarget(null)}
        />
      ) : null}

      {bulkOpen && self && allowBulkTransfer ? (
        <BulkSettleDialog
          self={self}
          selected={selectedRows}
          onClose={() => setBulkOpen(false)}
          onDone={() => {
            setBulkOpen(false)
            setSelection(new Map())
            setTruncated(null)
          }}
        />
      ) : null}

      {lockConfirm ? (
        <Modal
          open
          onClose={() => {
            if (!lockBusy) setLockConfirm(null)
          }}
          title={lockConfirm.unlocking ? 'Global Unlock' : 'Global Lock'}
          footer={
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={lockBusy}
                onClick={() => setLockConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={lockBusy}
                onClick={() => {
                  if (lockConfirm.kind === 'book') {
                    globalLockMut.mutate()
                    return
                  }
                  memberLockMut.mutate(lockConfirm.row.id)
                }}
              >
                {lockConfirm.unlocking ? 'Unlock' : 'Lock'}
              </Button>
            </>
          }
        >
          <p>
            {lockConfirm.kind === 'book'
              ? `Are you sure you want to ${lockConfirm.unlocking ? 'Unlock' : 'Lock'} globally?`
              : `Are you sure you want to ${lockConfirm.unlocking ? 'Unlock' : 'Lock'} ${lockConfirm.row.username} globally?`}
          </p>
          <p className="w7-dim">
            {lockConfirm.kind === 'book'
              ? lockConfirm.unlocking
                ? 'Betting reopens for the downline (except anyone you lock again).'
                : 'Betting locks for the whole downline. Except individual rows from the Global Lock column.'
              : lockConfirm.unlocking
                ? 'This account can place bets while the book stays globally locked.'
                : 'This account returns to the book-wide lock.'}
          </p>
        </Modal>
      ) : null}
    </>
  )
}
